import type { AccountView, ActivityView } from '../../api/admin';
import { ca } from '../../i18n/ca';

/** One row of the log as a sentence: who did it (an account, or a word for nobody), then what they did. */
export interface ActivityLine {
  /** The account that did it, linked to its profile; null when no account is behind the entry. */
  actor: AccountView | null;
  /** The word that stands for the actor when no account does («La plataforma», «Algú»), if the sentence has one. */
  lead: string | null;
  /** The rest of the sentence, in lower case when something precedes it. */
  text: string;
  /** The platform has no sentence for this kind: `text` says so, and the row shows the kind apart. */
  unknown: boolean;
}

const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

const nameOf = (a: AccountView | undefined) => (a ? a.displayName || a.handle : null);

/**
 * What the platform records of an admin acting on another account is filed under the account it
 * happened to, with `by <id>` as the detail and the admin nowhere in the entry's account: the
 * sentence the profile reads («Ha quedat desactivat») is right there and wrong in a log of
 * everybody, so these are written out with both people named. Everything else is the platform's
 * own sentence, which speaks of the account the entry is filed under, with that account's name in
 * front and, when no account is behind it, a word for nobody.
 */
export function activityLine(e: ActivityView, who: Map<number, AccountView>): ActivityLine {
  const t = ca.adminUi.act;
  const self = e.accountId == null ? undefined : who.get(e.accountId);
  const by = /^by (\d+)$/.exec(e.detail ?? '');
  const admin = by ? who.get(Number(by[1])) : undefined;
  const target = nameOf(self) ?? t.anAccount;
  const line = (
    actor: AccountView | null,
    text: string,
    lead: string | null = null,
  ): ActivityLine => ({
    actor,
    lead,
    text,
    unknown: false,
  });

  if (!e.text) {
    return { actor: self ?? null, lead: self ? null : t.someone, text: t.unknown, unknown: true };
  }

  // An admin acting on another account.
  if (admin) {
    switch (e.kind) {
      case 'account.disabled':
        return line(admin, t.disabled(target));
      case 'account.enabled':
        return line(admin, t.enabled(target));
      case 'role.granted':
        return line(admin, t.roleGranted(roleName(e.subject), target));
      case 'role.revoked':
        return line(admin, t.roleRevoked(roleName(e.subject), target));
      case 'identity.unlinked':
        return line(admin, t.identityUnlinked(providerName(e.subject), target));
    }
  }
  // An admin acting, filed under the admin with the other account's handle as the subject.
  if (e.kind === 'auth.password_changed' && e.detail === 'admin' && e.subject) {
    return line(self ?? null, t.passwordReset(e.subject), self ? null : t.anAdmin);
  }
  if (e.kind === 'account.anonymised' && e.subject) {
    return line(self ?? null, t.anonymised(e.subject), self ? null : t.anAdmin);
  }
  if (e.kind === 'account.registered' && (e.detail ?? '').startsWith('by admin')) {
    return line(null, t.createdByAdmin(e.subject ?? '?'), t.anAdmin);
  }
  // Nobody to name: the sentence leads with a subject of its own, or with a word for nobody.
  if (e.kind === 'session.commander_unknown') {
    return line(null, e.text, null);
  }
  if (self) {
    return line(self, lowerFirst(e.text));
  }
  const someone = e.kind === 'auth.sign_in_failed' || e.kind === 'auth.sign_in_throttled';
  return line(null, lowerFirst(e.text), someone ? t.someone : ca.adminUi.activityNobody);
}

function roleName(role: string | null): string {
  const names = ca.roleNames as Record<string, string>;
  return (role && names[role.toUpperCase()]) || role || '?';
}

function providerName(provider: string | null): string {
  const names = ca.providers as Record<string, string>;
  return (provider && names[provider.toUpperCase()]) || provider || '?';
}

/** The name an actor is shown under: the display name, else the handle. */
export const actorName = (a: AccountView) => a.displayName || a.handle;
